import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/media";
import { formatRating, typeLabel } from "@/utils/format";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-background-50">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 热门推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/70 to-background-50/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent"></div>

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                    热门推荐
                  </span>
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-500 text-background-50">
                    {typeLabel(item.type)}
                  </span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground-950 font-heading mb-4">
                  {item.title}
                </h2>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground-700 mb-4">
                  <span className="flex items-center gap-1 text-primary-400 font-semibold">
                    <i className="ri-star-fill"></i>
                    {formatRating(item.rating)}
                  </span>
                  <span>{item.year}</span>
                  <span className="w-px h-3 bg-foreground-700/50"></span>
                  <span>{item.region}</span>
                  <span className="w-px h-3 bg-foreground-700/50"></span>
                  <span>{item.genres.join(" · ")}</span>
                </div>
                <p className="hidden sm:block text-sm md:text-base text-foreground-600 leading-relaxed mb-6 line-clamp-2 max-w-xl">
                  {item.description}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    查看详情
                  </Link>
                  <Link
                    to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-secondary-400 text-foreground-800 text-sm font-medium hover:bg-secondary-500/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{typeLabel(item.type)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/50 text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/50 text-foreground-950 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${item.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-primary-500" : "w-3 bg-foreground-700/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}